import { inject, InjectionToken } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ConversationConfig, RouteConfig } from '@galaxy/conversation/core';
import { ConversationChannel, PlatformLocation } from '@vendasta/conversation';
import { filterNullAndUndefined } from '@vendasta/rx-utils';
import { GetAccountInfoResponse, OwnerType, ParticipantType, SmsService } from '@vendasta/smsv2';
import { combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { partnerId } from '../../globals';
import { isA2pSubmitRestricted } from '../core/a2p-restriction';
import { AccountGroup, AccountGroupService } from '../account-group';
import { AppConfigService } from '../app-config.service';
import { AuthService } from '../auth.service';
import { Brand } from '../brands/brand';
import { FeatureFlagService } from '../core/feature-flag.service';
import { ProductService } from '../core/product.service';
import { isAccountGroup, isBrand, LocationsService } from '../locations';
import { PageAccessService, PageId } from '../page-access';
import { getConfiguration$, GlobalParticipantType } from '@vendasta/protos/conversation/v1';
import type { Configuration } from '@vendasta/protos/conversation/v1';
import { getSMSSettingUrl } from './inbox.constants';

export const INBOX_CONFIG_FOR_CONVERSATION = new InjectionToken<ConversationConfig>(
  '[Business Center Client]: Token for Conversation config',
  {
    providedIn: 'root',
    factory: function (): ConversationConfig {
      const accountGroupService = inject(AccountGroupService);
      const featureFlagService = inject(FeatureFlagService);
      const pageAccessService = inject(PageAccessService);
      const appConfigService = inject(AppConfigService);
      const authService = inject(AuthService);
      const locationService = inject(LocationsService);

      const userId$ = toObservable(authService.userId).pipe(filterNullAndUndefined());

      const currentLocation$ = locationService.currentLocation$.pipe(
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const accountGroupId$ = currentLocation$.pipe(
        filter((loc) => loc !== null),
        distinctUntilChanged(),
        map((location) => {
          return isBrand(location) ? '' : (<AccountGroup>location).accountGroupId;
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const country$ = accountGroupService.currentAccountGroup$.pipe(
        map((ag) => ag?.country ?? ''),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const geographicalState$ = accountGroupService.currentAccountGroup$.pipe(
        map((ag) => ag?.state ?? ''),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const partnerId$ = of(partnerId);

      const partnerBrandName$ = appConfigService.branding$.pipe(
        map((branding) => branding.name),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const companyName$ = accountGroupService.currentAccountGroup$.pipe(
        map((ag) => ag?.companyName),
        filter<string>(Boolean),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const marketId$ = accountGroupService.currentAccountGroup$.pipe(
        map((ag) => ag?.marketId),
        filter<string>(Boolean),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const featureFlag$ = combineLatest([partnerId$, marketId$]).pipe(
        switchMap(([partnerId, marketId]) => {
          return featureFlagService.checkFeatureFlag(partnerId, marketId, 'conversation_microservice_messages');
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const platformLocation = PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP;

      const conversationChannelsEnabled$ = of([
        ConversationChannel.CONVERSATION_CHANNEL_SMS,
        ConversationChannel.CONVERSATION_CHANNEL_INTERNAL,
      ]);

      const routes$ = currentLocation$.pipe(
        map((location): RouteConfig => {
          if (!isAccountGroup(location)) {
            const loc = location.groupNodes[0];
            return {
              root: `/account/brands/${loc}`,
              sendNewMessage: `/account/brands/${loc}/inbox/new-message`,
            };
          }
          const loc = location.accountGroupId;
          return {
            root: `/account/location/${loc}`,
            sendNewMessage: `/account/location/${loc}/inbox/new-message`,
            getAiAssistantChatPath: (aiAssistantId: string) =>
              `/account/location/${loc}/inbox/ai-assistant/${aiAssistantId}/chat`,
            a2pForm: getSMSSettingUrl(loc),
          };
        }),
      );

      const isImpersonating$ = toObservable(authService.isImpersonating);
      const hasAccessToInboxPage$ = pageAccessService.isPageAccessible$(PageId.inbox);
      const allowSmbChatWithPartner$ = appConfigService.legacyConfig$.pipe(
        map((legacyConfig) => legacyConfig?.allowSmbChatWithPartner),
        distinctUntilChanged(),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const googleBusinessMessagesSupported$ = of(true);
      const facebookMessengerSupported$ = of(true);
      const instagramMessagesSupported$ = of(true);
      const whatsAppMessagesSupported$ = of(true);
      const smsEnabled$ = of(true);

      const hideNewMessageButton = true;

      const inboxConfig$ = accountGroupId$.pipe(
        switchMap((accountGroupId) => {
          if (!accountGroupId) {
            return of({} as Configuration);
          }
          return getConfiguration$({
            subjectParticipant: {
              participantType: GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP,
              internalParticipantId: accountGroupId,
            },
          }).pipe(
            map((response) => response.configuration),
            catchError(() => of({} as Configuration)),
          );
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

      const webChatEnabled$ = inboxConfig$.pipe(map((config) => config?.webChatEnabled || false));

      const whatsAppEnabled$ = inboxConfig$.pipe(map((config) => config?.whatsappEnabled || false));

      const smsAIResponderEnabled$ = inboxConfig$.pipe(
        map((config) => config?.aiConfiguration?.smsResponderEnabled || false),
      );

      const aiVoiceAssistantEnabled$ = inboxConfig$.pipe(map((config) => config?.aiVoiceEnabled || false));

      // WARP-2390: temporarily bypassing the email gate
      const emailEnabled$ = of(true);

      const groupId$ = currentLocation$.pipe(
        map((location) => {
          return isBrand(location) ? (location as Brand).groupNodes[0] : '';
        }),
      );

      return {
        userId$,
        accountGroupId$,
        country$,
        geographicalState$,
        partnerId$,
        partnerBrandName$,
        companyName$,
        featureFlag$,
        platformLocation,
        conversationChannelsEnabled$,
        routes$,
        marketId$,
        isImpersonating$,
        hasAccessToInboxPage$,
        allowSmbChatWithPartner$,
        googleBusinessMessagesSupported$,
        facebookMessengerSupported$,
        instagramMessagesSupported$,
        whatsAppMessagesSupported$,
        smsEnabled$,
        hideNewMessageButton,
        webChatEnabled$,
        groupId$,
        smsAIResponderEnabled$,
        aiVoiceAssistantEnabled$,
        whatsAppEnabled$,
        emailEnabled$,
      };
    },
  },
);

export const SmsRegistrationLibraryInjectionToken = new InjectionToken('dependencies given to the sms library', {
  factory: () => {
    const accountGroupService = inject(AccountGroupService);
    const smsService = inject(SmsService);
    const productService = inject(ProductService);

    const accountGroupId$ = accountGroupService.currentAccountGroupId$;
    const subAccountId$ = accountGroupId$.pipe(
      switchMap((accountGroupId) => {
        return smsService
          .getAccountInfo({
            internalId: accountGroupId,
            type: ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
          })
          .pipe(
            catchError((e) => {
              console.error('error retrieving GetAccountInfo:', e);
              return of({} as GetAccountInfoResponse);
            }),
          );
      }),
      catchError((e) => {
        console.error('error retrieving GetAccountInfo:', e);
        return of({} as GetAccountInfoResponse);
      }),
      filter((accountInfo) => !!accountInfo),
      map((accountInfo) => accountInfo?.subaccountId),
    );

    const isA2pSubmitRestricted$ = productService.activeProducts$.pipe(map(isA2pSubmitRestricted));

    return {
      ownerId$: accountGroupId$,
      ownerType: OwnerType.OWNER_TYPE_ACCOUNT_GROUP,
      subaccountID$: subAccountId$,
      isA2pSubmitRestricted$,
    };
  },
});
